import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MessagingPlatformIntegrationType,
  useListSlackChannelsQuery,
  useUpdateSlackIntegrationMutation,
} from "../../../__generated__/graphql";
import { useUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import SlackLogo from "../../../images/slackLogo.png";
import { INTEGRATIONS } from "../../../utils/constants";
import { SettingsTabNames } from "../../../utils/interfaces";
import IntegrationButton from "../../IntegrationButton";
import Section from "../Section";

const { SLACK, SLACK_INSTALLATION_URL } = INTEGRATIONS;

interface Props {
  section: string;
  tabName: SettingsTabNames;
  discardName: string;
  companyId?: number;
  clientId?: string;
  data?: MessagingPlatformIntegrationType;
}

interface SlackSettings {
  defaultEngineeringCollaborationChannel?: string | null;
}

const SlackIntegrationSection: React.FC<Props> = ({
  section,
  tabName,
  discardName,
  companyId,
  data,
}) => {
  const [settings, setSettings] = useState<SlackSettings>({
    defaultEngineeringCollaborationChannel: "",
  });
  const originalSettingsRef = useRef(settings);

  const { data: dataSlackChannels, loading } = useListSlackChannelsQuery({
    variables: { companyId },
    skip: !data,
  });

  const slackChannels = dataSlackChannels?.listSlackChannels || [];

  const [updateIntegration] = useUpdateSlackIntegrationMutation();
  const {
    setExternalSaveFunction,
    setExternalFunction,
    isDirty,
    markSettingAsChanged,
    clearUnsavedChanges,
  } = useUnsavedChanges();

  const handleInputChange = (field: keyof typeof settings, value: any) => {
    setSettings((prev) => {
      const updated = { ...prev, [field]: value };
      if (prev[field] !== value) markSettingAsChanged(tabName);
      return updated;
    });
  };

  const handleSave = useCallback(async () => {
    if (!isDirty(tabName) || !data) return;

    try {
      await updateIntegration({
        variables: {
          input: { ...settings },
          companyId,
        },
      });
      originalSettingsRef.current = settings;
      clearUnsavedChanges(tabName);
    } catch (err) {
      console.error("Slack save error:", err);
    }
  }, [
    isDirty,
    tabName,
    data,
    updateIntegration,
    settings,
    companyId,
    clearUnsavedChanges,
  ]);

  const handleDiscard = useCallback(async () => {
    setSettings(originalSettingsRef.current);
    clearUnsavedChanges(tabName);
  }, [clearUnsavedChanges, tabName]);

  useEffect(() => {
    if (data) {
      const defaultEngineeringCollaborationChannel =
        data?.slackIntegration?.defaultEngineeringCollaborationChannel;

      const newSettings = {
        defaultEngineeringCollaborationChannel,
      };

      setSettings(newSettings);
      originalSettingsRef.current = newSettings;
    }
  }, [data]);

  useEffect(() => {
    if (section === SLACK.toLowerCase()) {
      setExternalSaveFunction(tabName, handleSave);
      setExternalFunction(discardName, handleDiscard);
    }
  }, [
    section,
    handleSave,
    handleDiscard,
    setExternalSaveFunction,
    setExternalFunction,
    tabName,
    discardName,
  ]);

  return (
    <Section title={SLACK} hide={section !== SLACK.toLowerCase()}>
      {/* TODO - remove below once we have LLM service(s) in place */}
      <div className='text-3xl text-red-500 font-bold mb-4'>Alpha release</div>
      {data ? (
        <div className='space-y-4'>
          <IntegrationButton
            key={data.slackIntegration?.botUserId}
            logo={SlackLogo}
            // Display the organization name along with GitHub label.
            name={`${data.slackIntegration?.teamName}`}
            nameTextPreamble='Edit installation for organization:'
            // Link to a page for managing this particular integration.
            // to={`https://github.com/organizations/SailfishAI/settings/installations/${integration.slackIntegration?.teamId}`}
            to={`https://slack.com/marketplace/${data.slackIntegration?.appId}`}
          />
          <Autocomplete
            options={slackChannels}
            getOptionLabel={(option) => option.name}
            value={
              slackChannels.find(
                ({ id }) =>
                  id === settings.defaultEngineeringCollaborationChannel,
              ) || null
            }
            onChange={(event, value) =>
              handleInputChange(
                "defaultEngineeringCollaborationChannel",
                value?.id || "",
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loading}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Default Channel'
                variant='outlined'
              />
            )}
          />
          {/* Optionally, a button to add more installations */}
          {/* <IntegrationButton
                  logo={SlackLogo}
                  nameTextPreamble=''
                  name='Add more Slack organizations'
                  to={SLACK_INSTALLATION_URL}
                /> */}
        </div>
      ) : (
        <div className='flex items-center'>
          <IntegrationButton
            logo={SlackLogo}
            name={SLACK}
            to={SLACK_INSTALLATION_URL}
          />
        </div>
      )}
    </Section>
  );
};

export default SlackIntegrationSection;
